import { Anchor, Divider, Flex } from '@inflearn/ds-react-next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { AUTH_URL } from '../../constants/urls';
import { eventNames } from '../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../utils/GTM/useTrackMixpanelEvent';

type AuthAnchorKey = {
  findId: 'findId';
  findPassword: 'findPassword';
  signup: 'signup';
  signin: 'signin';
};

const authAnchors = {
  findId: {
    label: '아이디(이메일) 찾기',
    getHref: AUTH_URL.FIND_ID,
  },
  findPassword: {
    label: '비밀번호 찾기',
    getHref: AUTH_URL.FIND_PASSWORD,
  },
  signup: {
    label: '회원가입',
    getHref: AUTH_URL.SIGNUP,
  },
  signin: {
    label: '로그인',
    getHref: AUTH_URL.SIGNIN,
  },
} as const;

const ANCHOR_EVENT_NAME: Readonly<
  Record<keyof AuthAnchorKey, (typeof eventNames)[keyof typeof eventNames] | undefined>
> = {
  findId: eventNames.CLICK_FIND_ID_AT_SIGNIN,
  findPassword: eventNames.CLICK_FIND_PW_AT_SIGNIN,
  signup: eventNames.CLICK_SIGNUP_AT_SIGNIN,
  signin: undefined,
} as const;

interface AuthAnchorsProps {
  /**
   *  anchor 순서를 변경하고 싶으면 해당 props로 order를 전달
   */
  order?: (keyof AuthAnchorKey)[];
}

const AuthAnchors = ({ order = ['findPassword', 'signup', 'findId'] }: AuthAnchorsProps) => {
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { t } = useTranslation();
  const router = useRouter();

  const getHref = (anchorKey: keyof AuthAnchorKey) => {
    if (anchorKey !== 'signup') {
      return authAnchors[anchorKey].getHref();
    }

    const referUrl = Array.isArray(router.query.referUrl)
      ? router.query.referUrl[0]
      : router.query.referUrl;

    return authAnchors.signup.getHref({
      query: referUrl ? { referUrl } : {},
    });
  };

  const handleClickAnchor = (anchorKey: keyof AuthAnchorKey) => {
    const eventName = ANCHOR_EVENT_NAME[anchorKey];

    if (eventName === undefined) {
      return;
    }

    trackMixpanelEvent(eventName, {
      spot: 'page',
    });
  };

  return (
    <Flex gap={10} mb={32}>
      {order.map((anchorKey, index) => {
        const href = getHref(anchorKey);
        const label = authAnchors[anchorKey].label;

        return (
          <Fragment key={anchorKey}>
            <Anchor
              component={Link}
              c="gray.6"
              size="xs"
              href={href}
              onClick={() => handleClickAnchor(anchorKey)}
            >
              {t(label)}
            </Anchor>
            {index < order.length - 1 && <Divider orientation="vertical" size="xs" />}
          </Fragment>
        );
      })}
    </Flex>
  );
};

export default AuthAnchors;
